import React, { useCallback, useRef, useState, useEffect, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CButton } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { setListSegmentReport, setListSegmentReload } from '../../../../../actions/subscriber';
import { API_CLIENT_SEGMENT_REPORT, BQ_JOB_STATUS } from '../../../../../constants';
import { callTokenApi } from '../../../../../apiCaller';
import { camelCase, useOutsideHandling } from '../../../../../utils';
import SegmentDropdown from './SegmentDropdown';
import { EventExplorerContext, handleGetSortSegment } from '../EventExplorer';
import { positionPath, TooltipHover } from '../../../../general/TooltipHover';
import { iconChartPieNoColor } from '../../../../../assets/icons/logo-negative';
import { rectIntersection, DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from "@dnd-kit/utilities";
import CTooltip from '../../../../migration/CTooltip';

const SortableSegmentItem = ({ item, index, isDisabledRemoveSegment, onRemove }) => {
     const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
     const style = transform ? {
          transform: CSS.Translate.toString(transform),
          transition: transition,
          pointerEvents: 'none',
          ...(isDragging ? { zIndex: 1 } : {}),
     } : undefined;

     return (
          <div className="rule-segment d-flex align-item-center" ref={setNodeRef} style={style} {...attributes} {...listeners}>
               <div className="rule-segment-item d-flex align-items-center">
                    <div className="mr-2 rule-segment-item-icon d-flex align-items-center">
                         <CIcon
                              name="iconChartPie"
                              style={{ cursor: 'pointer', width: '16px', height: '16px', marginRight: '5px' }}
                         />
                         <span className="rule-segment-item-level">S{index + 1}</span>
                    </div>
                    <div className="d-flex align-items-center rule-segment-item-info">
                         <p
                              className="mr-3 mb-0"
                              style={{
                                   maxWidth: '50px',
                                   textOverflow: 'ellipsis',
                                   overflow: 'hidden',
                                   whiteSpace: 'nowrap',
                              }}
                         >
                              {item.description ? (
                                   <CTooltip
                                        content={<SegmentTooltipContent item={item} index={index} />}
                                        placement="top"
                                        advancedOptions={{
                                             onShow(instance) {
                                                  const contentEl = instance.popper.querySelector('.tippy-box');
                                                  if (contentEl) {
                                                       contentEl.classList.add('custom-tooltip-hover', 'badge', 'top');
                                                  }
                                             },
                                             arrow: positionPath["top"]

                                        }}
                                        interactive={true}
                                   >
                                        <span style={{display: 'block', width: '100%'}}>{item.name}</span>
                                   </CTooltip>
                              ) : (
                                   <>{item.name}</>
                              )}
                         </p>
                         <CIcon
                              name="iconClose"
                              className="icon-close"
                              style={{
                                   cursor: isDisabledRemoveSegment ? 'default' : 'pointer',
                                   width: '9px',
                                   height: '9px',
                                   pointerEvents: isDisabledRemoveSegment ? 'none' : 'auto',
                                   opacity: isDisabledRemoveSegment ? 0.5 : 1,
                                   zIndex: 10
                              }}
                              onPointerDown={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => onRemove(item.id)}
                         />
                    </div>
               </div>
          </div>
     );
};

const AddSegments = ({ listSegmentApplyOff }) => {
     const dispatch = useDispatch();
     const {
          reportName,
          segmentSticky,
          setSort,
          sort,
          filterMetric,
          disableSort,
          listSegmentReportApply,
          setListSegmentReportApply,
          showData,
          isSaveReport,
          defaultConfig,
          SFObject,
          reportType,
     } = useContext(EventExplorerContext);

     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const user = useSelector((state) => state.theme.user);
     const listSegmentReport = useSelector((state) => state.subscriber.listSegmentReport);
     const externalObjectReport = useSelector((state) => state.subscriber.externalObjectReport);
     const customExternalObjectReport = useSelector((state) => state.subscriber.customExternalObjectReport);
     const [showPopup, setShowPopup] = useState(false);
     const [segmentsSelected, setSegmentsSelected] = useState();
     const [saveLoading, setSaveLoading] = useState(false);
     const [segmentsEdit, setSegmentsEdit] = useState([]);
     const [tempSegmentsOrder, setTempSegmentsOrder] = useState(null);

     const segmentsSelectedItems = useMemo(() => {
          if (!segmentsSelected) return [];

          return tempSegmentsOrder || segmentsSelected;
     }, [tempSegmentsOrder, segmentsSelected]);

     const accountId = activeAccount.id;
     const userId = user.id;

     const isSegmentReorder = useMemo(() => {
          if (!tempSegmentsOrder || !segmentsSelected) return false;
          if (tempSegmentsOrder.length !== segmentsSelected.length) return true;

          return tempSegmentsOrder.some((item, index) => item.id !== segmentsSelected[index].id);
     }, [tempSegmentsOrder, segmentsSelected]);

     const isDisabledRemoveSegment = saveLoading;

     useEffect(() => {
          if (listSegmentApplyOff?.length > 0) {
               setSegmentsEdit(listSegmentApplyOff);
          }
     }, [listSegmentApplyOff]); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          setSegmentsSelected(listSegmentReportApply);
          setTempSegmentsOrder(null);
     }, [listSegmentReportApply]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleClickBubble = () => {
          if (!showData) {
               return;
          }
          setTimeout(() => {
               if (!showPopup) {
                    setShowPopup(true);
               }
          }, 100);
     };

     const clickOutside = useCallback(() => {
          setShowPopup(false);
          setSegmentsEdit([]);
     }, []);

     const wrapperRef = useRef(null);
     useOutsideHandling(wrapperRef, clickOutside);

     const handleConfirmDelete = (id) => {
          if (!showData) return;

          const segmentToClose = segmentsSelected.find((item) => item.id === id);

          if (!segmentToClose) return;


          const segmentsEdit = [
               {
                    id: segmentToClose.id,
                    segmentReportId: segmentToClose.segmentReportId,
                    apply: false,
                    applyChart: false,
                    type: 'update',
                    isUpdateAt: true,
               },
          ];
          const data = {
               reportName,
               reportType,
               accountId,
               segmentsEdit,
               segmentSticky,
               userId,
               isSaveReport,
               currentSegmentSticky: defaultConfig?.segmentSticky,
               listSegmentReportApply,
               object: SFObject,
               externalObjectReport: [...externalObjectReport, ...customExternalObjectReport.map((el) => { return { object: el.object, appearInReport: el.appearInReport, characteristicOtherObject: el.characteristicOtherObject } })],
          };
          const changeSort = !!filterMetric.length && !!sort.fieldName?.includes(camelCase(segmentsEdit[0].id));

          const handleApiResponse = (response) => {
               if (response.status === 200) {
                    const { segmentReport } = response.data;
                    const newUpdate = [...listSegmentReport].map((seg) => {
                         const existed = segmentReport.find((item) => item.segmentId === seg.id);
                         if (existed) {
                              seg.apply = existed.apply;
                              seg.orderBy = existed.updated_at;
                         }
                         return seg;
                    });
                    const newListSegmentApply = newUpdate.filter((item) => item.apply);
                    if (newListSegmentApply.length > 0) {
                         if (changeSort) {
                              setSort(
                                   handleGetSortSegment({
                                        listSegmentReportApply: newUpdate.filter((item) => item.apply && item.queryStatus === BQ_JOB_STATUS.DONE),
                                        filterMetric,
                                   })
                              );
                         }
                    } else if (filterMetric[0] && !disableSort) {
                         setSort({
                              fieldName: filterMetric[0].value,
                              isDesc: true,
                         });
                    }
                    dispatch(setListSegmentReport(newUpdate));
                    dispatch(setListSegmentReload(newUpdate));
                    setSegmentsSelected(newUpdate.filter((item) => item.apply && item.queryStatus === BQ_JOB_STATUS.DONE));
                    setListSegmentReportApply(
                         newUpdate
                              .filter((item) => item.apply && item.queryStatus === BQ_JOB_STATUS.DONE)
                              .sort((a, b) => {
                                   if (a.isCustom !== b.isCustom) {
                                        return a.isCustom ? 1 : -1;
                                   }
                                   return new Date(a.orderBy) - new Date(b.orderBy);
                              })
                    );
                    setSaveLoading(false);
                    toast.success('Remove segment success!');
               }
          };

          if (segmentsEdit?.length) {
               setSaveLoading(true);

               const isCallApi = !isSaveReport || segmentSticky;
               if (isCallApi) {
                    callTokenApi(`${API_CLIENT_SEGMENT_REPORT}`, 'PUT', data).then((response) => {
                         handleApiResponse(response);
                    });
               } else {
                    const segmentReport = data.segmentsEdit.map((seg) => {
                         return {
                              accountId: data.accountId,
                              segmentId: seg.id,
                              reportName: data.reportName,
                              updated_at: new Date(),
                              apply: seg.apply,
                              applyChart: seg.applyChart,
                              userId: data.userId,
                         };
                    });
                    const response = { status: 200, data: { segmentReport } };
                    handleApiResponse(response);
               }
          }
     };

     const handleDragEnd = (event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;

          const oldIndex = segmentsSelectedItems.findIndex((x) => x.id === active.id);
          const newIndex = segmentsSelectedItems.findIndex((x) => x.id === over.id);
          if (oldIndex === -1 || newIndex === -1) return;

          const items = arrayMove(segmentsSelectedItems, oldIndex, newIndex);

          const newOrderIsSame = segmentsSelected && items.every((item, index) => item.id === segmentsSelected[index].id);
          if (newOrderIsSame) {
               setTempSegmentsOrder(null);
          } else {
               setTempSegmentsOrder(items);
          }
     };

     const handleApplyReorder = () => {
          if (!tempSegmentsOrder) return;

          // Handle update segment report in explore report after re-order select
          const segmentsEdit = [...tempSegmentsOrder].map((seg, idx) => {
               const updated_at = new Date();
               updated_at.setSeconds(updated_at.getSeconds() + idx * 5);

               return {
                    ...seg,
                    type: 'update',
                    updated_at,
               };
          });

          const payload = {
               reportName,
               reportType,
               accountId,
               segmentsEdit,
               segmentSticky,
               userId,
               isSaveReport,
               currentSegmentSticky: defaultConfig?.segmentSticky,
               listSegmentReportApply,
               object: SFObject,
               externalObjectReport: [...externalObjectReport, ...customExternalObjectReport.map((el) => { return { object: el.object, appearInReport: el.appearInReport, characteristicOtherObject: el.characteristicOtherObject } })],
          };

          const handleApiResponse = (response) => {
               if (response.status !== 200) return;

               const { segmentReport } = response.data;
               const newUpdate = [...listSegmentReport].map((seg) => {
                    const existed = segmentReport.find((item) => item.segmentId === seg.id);
                    if (existed) {
                         seg.apply = existed.apply;
                         seg.orderBy = existed.updated_at;
                    }
                    return seg;
               });
               dispatch(setListSegmentReport(newUpdate));
               dispatch(setListSegmentReload(newUpdate));

               setSegmentsSelected(tempSegmentsOrder);
               setListSegmentReportApply(tempSegmentsOrder);
               setTempSegmentsOrder(null);

               setSaveLoading(false);
               toast.success('Re-order segments successfully!');
          };

          setSaveLoading(true);
          const isCallApi = !isSaveReport || segmentSticky;
          if (isCallApi) {
               callTokenApi(`${API_CLIENT_SEGMENT_REPORT}`, 'PUT', payload).then(handleApiResponse);
          } else {
               const segmentReport = segmentsEdit.map((seg, idx) => {
                    const updated_at = new Date();
                    updated_at.setSeconds(updated_at.getSeconds() + idx * 5);

                    return {
                         accountId,
                         segmentId: seg.id,
                         reportName,
                         updated_at,
                         apply: seg.apply,
                         applyChart: seg.applyChart,
                         userId,
                    };
               });
               const response = { status: 200, data: { segmentReport } };
               handleApiResponse(response);
          }
     };

     return (
          <div className="custom-segment-dropdown d-flex align-items-center">
               <div className="segment-action d-flex align-items-center">
                    <DndContext collisionDetection={rectIntersection} onDragEnd={handleDragEnd} >
                         <SortableContext items={segmentsSelectedItems.map((x) => x.id)} strategy={rectSortingStrategy}>
                              <div className="d-flex align-item-center flex-wrap justify-content-end box-segment">
                                   {segmentsSelectedItems.length > 0 ? (
                                        segmentsSelectedItems.map((item, index) => (
                                             <TooltipHover
                                                  key={item.id}
                                                  title={`S${index + 1}: ${item.name}`}
                                                  description={item.description}
                                                  icon={iconChartPieNoColor}
                                                  placement="top"
                                             >
                                                  <SortableSegmentItem
                                                       item={item}
                                                       index={index}
                                                       isDisabledRemoveSegment={isDisabledRemoveSegment}
                                                       saveLoading={saveLoading}
                                                       onRemove={handleConfirmDelete}
                                                  />
                                             </TooltipHover>
                                        ))
                                   ) : (
                                        <span className="mr-3 no-segment">No segments added</span>
                                   )}
                              </div>
                         </SortableContext>
                    </DndContext>

                    {segmentsSelectedItems.length > 1 && (
                         <div className="apply-reorder-container">
                              <CButton className="text-capitalize" disabled={saveLoading || !isSegmentReorder}  onClick={handleApplyReorder}>
                                   <i className="fal fa-check mr-1" />
                                   Apply Reordering
                              </CButton>
                         </div>
                    )}

                    <div className="btn-add-segments">
                         <CButton className="text-capitalize" onClick={handleClickBubble}>
                              <i className="fal fa-plus mr-1" />
                              Add Segments
                         </CButton>
                    </div>
               </div>
               {showPopup && (
                    <div className="segment-dropdown" ref={wrapperRef}>
                         <SegmentDropdown
                              accountId={accountId}
                              segmentsEdit={segmentsEdit}
                              setSegmentsEdit={setSegmentsEdit}
                              setSegmentsSelected={setSegmentsSelected}
                              setShowPopup={setShowPopup}
                         />
                    </div>
               )}
          </div>
     );
};

const SegmentTooltipContent = ({ item, index }) => (
     <div className="badge-tooltip-wrapper relative">
          {item.name && (
               <div className="badge-tooltip-head d-flex">
                    <CIcon icon={iconChartPieNoColor} className="icon" style={{ '--ci-color': 'white' }} />
                    <span className='hasIconClose'>{`S${index + 1}: ${item.name}`}</span>
               </div>
          )}

          {item.description && (
               <div className="badge-tooltip-des">
                    <span>{item.description}</span>
               </div>
          )}

     </div>
);

export default AddSegments;
